import { useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { Chapter, Reveal, ImagePlaceholder, Lightbox } from "@/components/atoms";

const WorkList = ({ items }) => (
  <ul className="flex flex-wrap gap-x-5 gap-y-2">
    {items.map((w, i) => (
      <li key={i} className="font-mono text-[10px] uppercase tracking-[0.15em] text-smoke border-b border-hairline pb-1">
        {w}
      </li>
    ))}
  </ul>
);

const CaseText = ({ item }) => (
  <>
    <Reveal>
      <div className="flex items-baseline gap-4 flex-wrap">
        <span className="font-mono text-[11px] tracking-[0.25em] text-proof">{item.num}</span>
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-smoke">{item.tag}</span>
      </div>
      <h3 className="mt-5 font-serif font-light tracking-tight leading-[1.02] text-3xl sm:text-4xl md:text-5xl" data-testid={`case-${item.id}-title`}>
        {item.name}
      </h3>
      <p className="mt-3 font-serif italic text-xl md:text-2xl text-smoke">{item.claim}</p>
    </Reveal>
    <div className="mt-8 space-y-5">
      {item.body.map((para, i) => (
        <Reveal key={i} delay={0.1 + i * 0.08}>
          <p className="text-base md:text-lg font-light leading-relaxed text-ink/85">{para}</p>
        </Reveal>
      ))}
    </div>
    <Reveal delay={0.2}>
      <div className="mt-8 border-l-2 border-proof pl-5 py-1">
        <p className="font-serif italic text-lg md:text-xl leading-snug" data-testid={`case-${item.id}-proof`}>
          {item.proof}
        </p>
      </div>
    </Reveal>
    {item.link && (
      <Reveal delay={0.25}>
        <a
          data-testid={`case-${item.id}-art-link`}
          href={item.link.url}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-8 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-ink link-underline"
        >
          {item.link.label} <span className="text-proof">↗</span>
        </a>
      </Reveal>
    )}
    <Reveal delay={0.3}>
      <div className="mt-10">
        <WorkList items={item.work} />
      </div>
    </Reveal>
  </>
);

const CaseBlock = ({ item, index, platesLabel, galleryLabel, enlargeLabel, awaiting, onZoom }) => {
  const hasPhotos = item.plates.some((p) => p.src);
  const zoomables = item.plates.filter((p) => p.src);
  const handleZoom = (plateIdx) => {
    const zIndex = item.plates.slice(0, plateIdx + 1).filter((p) => p.src).length - 1;
    onZoom({ items: zoomables.map((p) => ({ src: p.src, label: p.label ?? p })), index: zIndex });
  };

  if (!hasPhotos) {
    return (
      <article className="border-t border-hairline" data-testid={`case-${item.id}`}>
        <div className="px-5 md:px-10 py-16 md:py-24 max-w-4xl">
          <CaseText item={item} />
        </div>
      </article>
    );
  }

  const flip = index % 2 === 1;
  const gridCols = item.plates.length > 4 ? "grid-cols-2" : "grid-cols-1 sm:grid-cols-2";
  return (
    <article className="border-t border-hairline" data-testid={`case-${item.id}`}>
      <div className="px-5 md:px-10 py-16 md:py-24">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14">
          <div className={`lg:col-span-5 ${flip ? "lg:order-2" : ""}`}>
            <CaseText item={item} />
          </div>
          <div className={`lg:col-span-7 ${flip ? "lg:order-1" : ""}`}>
            <Reveal delay={0.15}>
              <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-smoke/70 mb-4">{galleryLabel}</p>
            </Reveal>
            <div className={`grid ${gridCols} gap-4 md:gap-5`}>
              {item.plates.map((plate, i) => {
                const label = plate.label ?? plate;
                const frame = (
                  <ImagePlaceholder
                    label={label}
                    awaiting={awaiting}
                    src={plate.src}
                    pos={plate.pos}
                    ratio={plate.ratio ?? (i === 0 ? "aspect-[16/9]" : "aspect-[4/3]")}
                    testid={`case-${item.id}-plate-${i}`}
                  />
                );
                return (
                  <Reveal key={i} delay={0.1 + i * 0.06} className={i === 0 ? "sm:col-span-2" : ""}>
                    {plate.src ? (
                      <button
                        data-testid={`case-${item.id}-zoom-${i}`}
                        onClick={() => handleZoom(i)}
                        className="block w-full cursor-zoom-in"
                        aria-label={`${enlargeLabel}: ${label}`}
                      >
                        {frame}
                      </button>
                    ) : (
                      frame
                    )}
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </article>
  );
};

export const CaseStudies = () => {
  const { t } = useLanguage();
  const c = t.cases;
  const [lightbox, setLightbox] = useState(null);
  return (
    <section id="arbeiten" className="border-t border-hairline" data-testid="cases-section">
      <div className="px-5 md:px-10 pt-20 md:pt-28 pb-12 md:pb-16">
        <Reveal>
          <Chapter num={c.chapter} label={c.label} />
        </Reveal>
        <div className="mt-10 grid md:grid-cols-12 gap-8 items-end">
          <Reveal delay={0.1} className="md:col-span-8">
            <h2 className="font-serif font-light tracking-tight leading-none text-4xl sm:text-5xl md:text-7xl" data-testid="cases-headline">
              {c.headline}
            </h2>
          </Reveal>
          <Reveal delay={0.2} className="md:col-span-4">
            <p className="text-base md:text-lg font-light leading-relaxed text-smoke">{c.intro}</p>
          </Reveal>
        </div>
      </div>
      {c.items.map((item, i) => (
        <CaseBlock
          key={item.id}
          item={item}
          index={i}
          platesLabel={c.platesLabel}
          galleryLabel={c.galleryLabel}
          enlargeLabel={c.enlargeLabel}
          awaiting={t.hero.plateAwaiting}
          onZoom={setLightbox}
        />
      ))}
      <Lightbox gallery={lightbox} onClose={() => setLightbox(null)} closeLabel={c.closeLabel} prevLabel={c.prevLabel} nextLabel={c.nextLabel} />
    </section>
  );
};
